/**
 * MetricCell — one served value in its trust envelope (the design's framed atom):
 * framed value + performance arrow, RAG left-border + tint, padlock (locked) / warning
 * (divergent), dq badge (OPEX visibly reads KNOWN-WRONG), honest em-dash for NULL.
 * Freshness rides the title.
 *
 * THE ARROW IS PERFORMANCE, NOT THE METRIC'S DIRECTION (corrected 10 Aug 2026). It used
 * to render higher_is_better/lower_is_better — a fixed property of the metric, so every
 * cell wore the same confident arrow whether or not anything had been measured, including
 * months with no target to beat. It now reads the RAG the resolver already computed:
 * up = met or beat the target, down = missed it, and NOTHING until there is both an actual
 * and a target to compare. A duplicate of the colour by design — the same verdict on a
 * second, non-colour channel. The metric's own direction lives in its definition popover.
 *
 * S3: when drill.enabled and a handler is wired, the VALUE is the drill affordance
 * (the anchor carries the envelope href for right-click/share). Props are Cell
 * objects, never naked numerics (CD-7); a non-Cell renders a visible refusal.
 */

import React from 'react'
import clsx from 'clsx'
import { AlertTriangle, ArrowDown, ArrowUp, Lock } from 'lucide-react'
import type { Cell, Rag } from '../../types/metrics'
import { dqBadge, formatCellValue, isCell, RAG_CLASSES } from './cellFormat'
import TrustChip from './TrustChip'

interface MetricCellProps {
  cell: Cell
  target?: Cell | null
  onDrill?: (cell: Cell) => void
}

function CellValue({ cell, ragClass, onDrill }: { cell: Cell; ragClass: string; onDrill?: (cell: Cell) => void }) {
  const value = (
    <span data-testid="metric-cell-value" className={clsx('font-semibold tabular-nums', ragClass)}>
      {formatCellValue(cell)}
    </span>
  )
  if (!cell.drill.enabled || !onDrill) return value
  return (
    <a
      href={cell.drill.href ?? '#'}
      onClick={(event) => {
        event.preventDefault()
        onDrill(cell)
      }}
      aria-label={`Drill into ${cell.served_metric} ${cell.period}`}
      className="underline decoration-dotted decoration-mut2 underline-offset-2 hover:decoration-solid"
    >
      {value}
    </a>
  )
}

/** Up beat the target, down missed it, nothing when there is no verdict to give —
 *  rag is 'none' whenever the value or the target is absent (resolver §5 _rag_for). */
function PerformanceArrow({ rag }: { rag: Rag }) {
  if (rag === 'none') return null
  const Arrow = rag === 'green' ? ArrowUp : ArrowDown
  const label = rag === 'green' ? 'met or beat target' : 'below target'
  return <Arrow size={11} aria-label={label} className="text-mut2 flex-shrink-0" />
}

function LockGlyphs({ cell }: { cell: Cell }) {
  return (
    <>
      {cell.lock_state === 'locked' && <Lock size={10} aria-label="Locked month" className="text-mut2" />}
      {cell.lock_state === 'divergent' && (
        <AlertTriangle size={10} aria-label="Locked value restated since lock" className="text-amber" />
      )}
    </>
  )
}

/** What a value from an unclosed month IS, marked on the cell.
 *
 * ⚠ THE CELL SAID NOTHING AT ALL UNTIL 24 Aug 2026, and the open column is where the matrix
 * misleads hardest. On Kirkstall 2026-08 the blend serves Revenue £614,910 — a projection of
 * the WHOLE month — beside COGS £3,265, OPEX £25,512 and EBITDA £154,444, which are only what
 * has posted so far. The column reads as a record month at a 98.2% gross margin against 30% in
 * July. Every figure is correct; none of them describes the same span.
 *
 * ⚠ TWO MARKS, NOT ONE, because they are two different claims and merging them would hide the
 * mismatch that IS the problem. `~` is a forecast of the whole period; `·` is actual-to-date.
 * A column showing both cannot be read down as a P&L or across as a margin, and the legend
 * says so. The RAG is already suppressed here (resolver._rag_for), so the cell is neutral —
 * this is what stops it reading as neutral-but-real. */
function PeriodGlyph({ cell }: { cell: Cell }) {
  if (!cell.part_period) return null
  return cell.forecast ? (
    <span aria-label="Forecast for the whole month" title="Forecast for the whole month — this month has not closed" className="font-mono text-[10px] leading-none text-amber">~</span>
  ) : (
    <span aria-label="Actual to date" title="Actual to date — this month has not closed, so cost is still posting" className="font-mono text-[11px] leading-none text-mut2">·</span>
  )
}

/** Framed treatment: RAG left-border + tint carry the state; the value + arrow lead. */
function framedStyle(rc: string, knownWrong: boolean): React.CSSProperties {
  const bad = knownWrong || rc === 'red'
  const borderVar = bad ? 'var(--bad)' : rc === 'green' ? 'var(--good)' : 'var(--neutral)'
  const tint = bad ? 'var(--bad-bg)' : rc === 'green' ? 'var(--good-bg)' : 'transparent'
  return { borderLeft: `2.5px solid ${borderVar}`, background: tint }
}

export default function MetricCell({ cell, target, onDrill }: MetricCellProps): React.JSX.Element {
  if (!isCell(cell)) return <span className="text-[10px] italic text-bad">invalid cell</span>
  const badge = dqBadge(cell.dq_flags)
  const rc = cell.value === null ? 'none' : cell.rag
  const ragClass = RAG_CLASSES[rc]
  const knownWrong = badge?.tone === 'bad'
  const title = cell.freshness ? `as of ${cell.freshness}` : undefined
  const tgt = target && isCell(target) ? <span className="text-[10px] tabular-nums text-mut2 font-mono">tgt {formatCellValue(target)}</span> : null
  const badgeEl = badge ? <TrustChip tone={badge.tone} label={badge.label} title={cell.dq_flags.join(' · ')} /> : null

  return (
    <div data-rag={rc} title={title} style={framedStyle(rc, knownWrong)} className="flex flex-col items-end gap-px pl-2 pr-2.5 py-1.5 rounded-sm">
      <div className="flex items-center justify-end gap-1">
        <LockGlyphs cell={cell} />
        <PeriodGlyph cell={cell} />
        <CellValue cell={cell} ragClass={ragClass} onDrill={onDrill} />
        <PerformanceArrow rag={rc} />
      </div>
      <div className="flex items-center justify-end gap-1.5 mt-0.5">
        {tgt}
        {badgeEl}
      </div>
    </div>
  )
}
